/**
 * 移动端轮播图组件
 * 展示推荐游戏（热度Top 5）
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatured } from '../../hooks/use-featured';
import { useHistory } from '../../hooks/use-history';
import { buildImageUrl } from '../../utils/image-url';
import { calculateHeatScore, formatHeatScore } from '../../utils/heat-score';
import { DegradedBadge } from '../ui/badge';
import { Flame, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './mobile-carousel.module.css';

export const MobileCarousel: React.FC = () => {
  const navigate = useNavigate();
  const { data: featuredResult, isLoading } = useFeatured();
  const { addHistory } = useHistory();
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const games = featuredResult?.data ?? [];
  const isDegraded = featuredResult?.fallback;
  const degradedMessage = featuredResult?.message;

  // 自动播放
  useEffect(() => {
    if (!isAutoPlaying || games.length === 0) return;

    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % games.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [isAutoPlaying, games.length]);

  // 切换轮播
  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + games.length) % games.length);
  }, [games.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % games.length);
  }, [games.length]);

  // 暂停自动播放
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  // 触摸滑动支持
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoPlaying(false);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
    setIsAutoPlaying(true);
  };

  // 点击游戏
  const handleGameClick = (game: typeof games[0]) => {
    addHistory(game);
    navigate(`/game/${game.slug || game.id}`);
  };

  if (isLoading) {
    return <div className={styles.skeleton}>加载中...</div>;
  }

  if (games.length === 0) {
    return null;
  }

  return (
    <div 
      className={styles.container}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* 降级标记 */}
      {isDegraded && (
        <div className={styles.degradedBadge}>
          <DegradedBadge message={degradedMessage} />
        </div>
      )}

      {/* 轮播内容 - 只渲染当前活动幻灯片 */}
      <div className={styles.slides}>
        {(() => {
          const game = games[currentIndex];
          const gameHeatScore = calculateHeatScore(game);
          
          return (
            <div
              key={game.id}
              className={styles.slide}
              onClick={() => {
                setIsAutoPlaying(false);
                handleGameClick(game);
              }}
            >
              <img
                src={buildImageUrl(game.cover)}
                alt={game.title}
                className={styles.image}
                loading="eager"
              />
              <div className={styles.overlay}>
                <h3 className={styles.title}>{game.title}</h3>
                <div className={styles.heat}>
                  <Flame className={styles.heatIcon} />
                  <span>{formatHeatScore(gameHeatScore)}</span>
                </div>
                <button 
                  className={styles.playButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAutoPlaying(false);
                    handleGameClick(game);
                  }}
                >
                  开始游戏
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* 左右切换按钮 */}
      <button className={`${styles.navButton} ${styles.prev}`} onClick={goToPrev}>
        <ChevronLeft />
      </button>
      <button className={`${styles.navButton} ${styles.next}`} onClick={goToNext}>
        <ChevronRight />
      </button>

      {/* 指示器 */}
      <div className={styles.indicators}>
        {games.map((_, index) => (
          <button
            key={index}
            className={`${styles.indicator} ${index === currentIndex ? styles.active : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};
